import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { ReactElement, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { PaymentNetInputPageFlatFields } from 'src/app/RegisterAccount/RegisterAccount'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface RegisterAccountPaymentNetInputFormProps {
  className?: string
  flatFields: PaymentNetInputPageFlatFields
  flatFieldsErrors: Partial<
    Record<
      keyof PaymentNetInputPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  onFlatFieldChange: (
    fieldName: keyof PaymentNetInputPageFlatFields,
    value: PaymentNetInputPageFlatFields[keyof PaymentNetInputPageFlatFields],
  ) => void
  onSubmit: (bin?: string) => void
  stepElement?: ReactElement
  title: React.ReactElement
  subtitle: React.ReactElement
  cardInfoHeader: React.ReactElement
  submitButtonText?: React.ReactElement
  secondaryOption?: React.ReactElement
  topLevelError?: string
  loading?: boolean
}

const RegisterAccountPaymentNetinputForm: React.FC<
  RegisterAccountPaymentNetInputFormProps
> = ({
  className,
  flatFields,
  flatFieldsErrors,
  onFlatFieldChange,
  onSubmit,
  stepElement,
  title,
  subtitle,
  cardInfoHeader: paymentNetOrgIdHeader,
  submitButtonText,
  secondaryOption,
  topLevelError,
  loading,
}): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const [_paymentNetOrgIdFocus, setPaymentNetOrgIdFocus] = useState(false)
  const [_paymentNetUserIdFocus, setPaymentNetUserIdFocus] = useState(false)
  const [_paymentGroupIdFocus, setPaymentNetGroupIdFocus] = useState(false)
  const [_last4Focus, setLast4Focus] = useState(false)

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    onSubmit(flatFields?.paymentNetOrgId)
  }

  const formFieldsRefObj = {
    paymentNetOrgId: useRef<HTMLInputElement>(null),
    paymentNetUserId: useRef<HTMLInputElement>(null),
    paymentGroupId: useRef<HTMLInputElement>(null),
    last4: useRef<HTMLInputElement>(null),
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 60px 60px 40px 60px;
      `}
      className={className}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          max-width: 440px;
        `}
      >
        {stepElement}
        <span>{title}</span>
      </div>
      <div
        css={css`
          margin-top: 30px;
          max-width: 500px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            max-width: none;
          `)}
          margin-bottom: 20px;
        `}
      >
        {subtitle}
      </div>
      <form
        css={css`
          display: flex;
          flex-direction: column;
        `}
        onSubmit={handleOnSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {paymentNetOrgIdHeader}
          <div
            ref={formFieldsRefObj.paymentNetOrgId}
            css={css`
              margin-top: 20px;
            `}
          >
            <TextInputField
              errors={flatFieldsErrors['paymentNetOrgId']}
              design={TextInputFieldDesigns.New}
              placeholder={translateSingleString(
                locale,
                'Enter your PaymentNet Org ID',
              )}
              fieldLabel={
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  <Trns source={'PaymentNet Org ID'} />
                </span>
              }
              value={flatFields?.paymentNetOrgId?.toString() || ''}
              onFocus={(): void => setPaymentNetOrgIdFocus(true)}
              onBlur={(): void => setPaymentNetOrgIdFocus(false)}
              onChange={(value: string): void =>
                onFlatFieldChange('paymentNetOrgId', value)
              }
            />
          </div>
          <div
            ref={formFieldsRefObj.paymentNetUserId}
            css={css`
              margin-top: 20px;
            `}
          >
            <TextInputField
              errors={flatFieldsErrors['paymentNetUserId']}
              design={TextInputFieldDesigns.New}
              placeholder={translateSingleString(
                locale,
                'Enter PaymentNet User ID',
              )}
              fieldLabel={
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  <Trns source={'PaymentNet User ID'} />
                </span>
              }
              value={flatFields?.paymentNetUserId?.toString() || ''}
              onFocus={(): void => setPaymentNetUserIdFocus(true)}
              onBlur={(): void => setPaymentNetUserIdFocus(false)}
              onChange={(value: string): void =>
                onFlatFieldChange('paymentNetUserId', value)
              }
            />
          </div>
          <div
            ref={formFieldsRefObj.paymentGroupId}
            css={css`
              margin-top: 20px;
            `}
          >
            <TextInputField
              errors={flatFieldsErrors['paymentGroupId']}
              design={TextInputFieldDesigns.New}
              placeholder={translateSingleString(
                locale,
                'Enter Payment Group ID',
              )}
              fieldLabel={
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  <Trns source={'Payment Group ID'} />
                </span>
              }
              value={flatFields?.paymentGroupId?.toString() || ''}
              onFocus={(): void => setPaymentNetGroupIdFocus(true)}
              onBlur={(): void => setPaymentNetGroupIdFocus(false)}
              onChange={(value: string): void =>
                onFlatFieldChange('paymentGroupId', value)
              }
            />
          </div>
          <div
            ref={formFieldsRefObj.last4}
            css={css`
              margin-top: 20px;
            `}
          >
            <TextInputField
              errors={flatFieldsErrors['last4']}
              design={TextInputFieldDesigns.New}
              placeholder={translateSingleString(
                locale,
                'Enter your Central Billing Account Last 4-Digits',
              )}
              fieldLabel={
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  <Trns source={'Central Billing Account Last 4-Digits'} />
                </span>
              }
              value={flatFields?.last4?.toString() || ''}
              onFocus={(): void => setLast4Focus(true)}
              onBlur={(): void => setLast4Focus(false)}
              onChange={(value: string): void =>
                onFlatFieldChange('last4', value)
              }
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            gap: 24px;
            margin-top: 40px;
            align-items: center;
          `}
        >
          <Button
            type={ButtonTypes.submit}
            design={ButtonDesigns.designCta}
            text={submitButtonText || <Trns source={'Continue'} />}
            big
            loading={loading}
          />
          {secondaryOption}
        </div>
        {topLevelError !== undefined && (
          <span
            css={css`
              margin-top: 40px;
              ${getFontCss(
                14,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};

              ${getBreakpointDown(breakpointSizes.XSM)(css`
                margin-top: 30px;
              `)}
            `}
          >
            {topLevelError}
          </span>
        )}
      </form>
    </div>
  )
}

export default RegisterAccountPaymentNetinputForm
